import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Landmark, Loader2, AlertCircle, Check, ArrowRight, CornerDownLeft } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
import { bankBalanceApi } from '../services/bankBalanceApi';
import type { WeeklyAccount, WeeklyBalances } from '../types/bankBalance';
import { parseBankAmount, formatBankAmount } from '../utils/bankAmountParse';
import { InstallPrompt } from '../components/expense/InstallPrompt';

/**
 * Quick entry for the weekly bank balances (decision Roman, 2026-10-08): every active account
 * of the user's company, one field each, one save. Built for the country owners on a phone;
 * /bank-balance stays the full admin and analysis view.
 */

const COMPANIES = ['bin', 'dbi', 'dch', 'dfr', 'dit', 'dpt', 'dsp', 'dtr'];

const shortDate = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`)
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

function compactDelta(n: number): string {
    const abs = Math.abs(n);
    const s = abs >= 1e6 ? `${(abs / 1e6).toFixed(1)}M` : abs >= 1e3 ? `${Math.round(abs / 1e3)}K` : abs.toFixed(0);
    return `${n >= 0 ? '+' : '−'}${s}`;
}

export default function BankBalanceWeeklyPage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/bank-balance');
    const { user } = useAuth();
    const isAdmin = user?.isAdmin ?? false;
    const { toast } = useToast();

    const [date, setDate] = useState<string | undefined>(undefined);
    const [company, setCompany] = useState<string | undefined>(undefined);
    const [data, setData] = useState<WeeklyBalances | null>(null);
    const [values, setValues] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async (d?: string, c?: string) => {
        setLoading(true);
        setError(null);
        try {
            const res = await bankBalanceApi.getWeekly(d, c);
            setData(res);
            setDate(res.date);
            setValues(Object.fromEntries(res.accounts.map(a => [
                a.BankID, a.CurrentAmount !== null ? formatBankAmount(Number(a.CurrentAmount)) : '',
            ])));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not load the bank accounts.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!permLoading && canView) void load();
    }, [permLoading, canView, load]);

    const rows = useMemo(() => (data?.accounts ?? []).map(a => {
        const raw = values[a.BankID] ?? '';
        const parsed = raw.trim() ? parseBankAmount(raw) : null;
        const invalid = raw.trim() !== '' && parsed === null;
        const current = a.CurrentAmount !== null ? Number(a.CurrentAmount) : null;
        const dirty = parsed !== null && parsed !== current && !a.CurrentIsAutomatic;
        return { a, raw, parsed, invalid, current, dirty };
    }), [data, values]);

    const dirtyRows = rows.filter(r => r.dirty);
    const anyInvalid = rows.some(r => r.invalid);
    const missing = rows.filter(r => r.current === null && r.parsed === null).length;

    const save = async () => {
        if (!data || !date || dirtyRows.length === 0 || anyInvalid) return;
        setSaving(true);
        try {
            const res = await bankBalanceApi.saveWeekly({
                postingDate: date,
                companyCode: isAdmin ? data.companyCode : undefined,
                items: dirtyRows.map(r => ({ bankId: r.a.BankID, amountFCY: r.parsed as number })),
            });
            const n = res.inserted + res.updated;
            toast(`${n} balance${n === 1 ? '' : 's'} saved for ${shortDate(date)}.`, 'success');
            await load(date, isAdmin ? data.companyCode : undefined);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Saving failed.', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (permLoading) {
        return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
    }
    if (!canView) {
        return (
            <div className="p-4 sm:p-6 max-w-xl mx-auto">
                <div className="flex items-center gap-2 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>You do not have access to the bank balances.</span>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-xl mx-auto px-4 pt-4 sm:px-6 sm:pt-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <Landmark className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <h1 className="text-xl font-bold text-gray-900">Weekly bank balances</h1>
                </div>
                <Link to="/bank-balance" className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 whitespace-nowrap pt-1">
                    Full view <ArrowRight className="w-4 h-4" />
                </Link>
            </div>

            {/* Company + date */}
            <div className="mt-4 grid grid-cols-[auto_1fr] gap-3 items-end">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Company</label>
                    {isAdmin ? (
                        <select
                            value={data?.companyCode ?? ''}
                            onChange={e => { setCompany(e.target.value); void load(date, e.target.value); }}
                            className="h-11 bg-white border border-gray-300 rounded-lg px-3 text-base uppercase"
                        >
                            {COMPANIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                        </select>
                    ) : (
                        <div className="h-11 flex items-center px-3 rounded-lg bg-gray-100 text-base font-medium text-gray-700 uppercase">
                            {data?.companyCode ?? '—'}
                        </div>
                    )}
                </div>
                <div>
                    <label htmlFor="balance-date" className="block text-xs font-medium text-gray-500 mb-1">Balance date</label>
                    <input
                        id="balance-date"
                        type="date"
                        value={date ?? ''}
                        max={data?.today}
                        onChange={e => { if (e.target.value) void load(e.target.value, isAdmin ? (company ?? data?.companyCode) : undefined); }}
                        className="h-11 w-full bg-white border border-gray-300 rounded-lg px-3 text-base"
                    />
                </div>
            </div>
            <p className="mt-2 text-sm text-gray-500">
                Enter each balance as your bank shows it on this date, in the account currency.
            </p>
            <div className="mt-3 sm:hidden">
                <InstallPrompt
                    title="Install Dolder Reporting"
                    pitch="Add the app to your Home Screen to enter the weekly bank balances with one tap:"
                    dismissKey="bankBalanceInstallDismissed"
                />
            </div>

            {/* Accounts */}
            {loading ? (
                <div className="flex items-center justify-center h-40"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
            ) : error ? (
                <div className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" /><span>{error}</span>
                </div>
            ) : rows.length === 0 ? (
                <div className="mt-4 text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    No active bank accounts are configured for this company. Please contact Roman Dill.
                </div>
            ) : (
                <ul className="mt-4 space-y-3">
                    {rows.map(r => (
                        <AccountCard
                            key={r.a.BankID}
                            account={r.a}
                            raw={r.raw}
                            parsed={r.parsed}
                            invalid={r.invalid}
                            saved={r.current !== null && !r.dirty && !r.invalid}
                            disabled={!canEdit || !!r.a.CurrentIsAutomatic || saving}
                            onChange={v => setValues(prev => ({ ...prev, [r.a.BankID]: v }))}
                        />
                    ))}
                </ul>
            )}

            {/* Save bar */}
            {canEdit && rows.length > 0 && !loading && (
                <div className="sticky bottom-0 -mx-4 sm:-mx-6 mt-4 px-4 sm:px-6 pt-3 bg-white/95 backdrop-blur border-t border-gray-200"
                     style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || dirtyRows.length === 0 || anyInvalid}
                        className="w-full h-12 flex items-center justify-center gap-2 rounded-lg bg-blue-600 text-white text-base font-semibold
                                   hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500"
                    >
                        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                        {anyInvalid
                            ? 'Correct the amounts first'
                            : dirtyRows.length === 0
                                ? (missing === 0 ? 'All balances saved' : 'Enter the balances above')
                                : `Save ${dirtyRows.length} balance${dirtyRows.length === 1 ? '' : 's'}`}
                    </button>
                    {anyInvalid && <p className="mt-2 text-sm text-red-600 text-center">Please correct the amounts marked in red.</p>}
                    {!anyInvalid && missing > 0 && dirtyRows.length > 0 && (
                        <p className="mt-2 text-sm text-gray-500 text-center">{missing} account{missing === 1 ? '' : 's'} still without a balance for this date.</p>
                    )}
                </div>
            )}
        </div>
    );
}

function AccountCard({ account: a, raw, parsed, invalid, saved, disabled, onChange }: {
    account: WeeklyAccount;
    raw: string;
    parsed: number | null;
    invalid: boolean;
    saved: boolean;
    disabled: boolean;
    onChange: (v: string) => void;
}) {
    const last = a.LastAmount !== null ? Number(a.LastAmount) : null;
    const ccy = a.CurrencyCode ?? '';
    const inputId = `amt-${a.BankID}`;
    return (
        <li className={`rounded-xl border bg-white p-4 ${saved ? 'border-green-300' : 'border-gray-200'}`}>
            <div className="flex items-start justify-between gap-2">
                <label htmlFor={inputId} className="font-semibold text-gray-900 leading-snug">{a.BankAccountName}</label>
                <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded px-2 py-0.5">{ccy}</span>
            </div>
            <div className="mt-1 text-sm text-gray-500">
                {last !== null && a.LastDate
                    ? <>Last: <span className="tabular-nums">{formatBankAmount(last)}</span> · {shortDate(a.LastDate)}</>
                    : 'No earlier balance'}
            </div>

            {a.CurrentIsAutomatic ? (
                <div className="mt-3 text-sm text-gray-600">
                    Automatic balance: <span className="tabular-nums font-medium">{formatBankAmount(Number(a.CurrentAmount))}</span>
                </div>
            ) : (
                <>
                    <div className="mt-3 flex gap-2">
                        <input
                            id={inputId}
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            enterKeyHint="next"
                            placeholder="Balance"
                            value={raw}
                            disabled={disabled}
                            onChange={e => onChange(e.target.value)}
                            className={`min-w-0 flex-1 h-12 rounded-lg border px-3 text-lg text-right tabular-nums
                                        focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50
                                        ${invalid ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                        />
                        {last !== null && !raw.trim() && !disabled && (
                            <button
                                type="button"
                                onClick={() => onChange(formatBankAmount(last))}
                                className="h-12 px-3 flex items-center gap-1 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                                title="Use the last balance (unchanged)"
                            >
                                <CornerDownLeft className="w-4 h-4" /> Same
                            </button>
                        )}
                    </div>
                    <div className="mt-1.5 min-h-[1.25rem] text-sm">
                        {invalid ? (
                            <span className="text-red-600">Not a valid amount</span>
                        ) : parsed !== null ? (
                            <span className="text-gray-500">
                                <span className="tabular-nums">{formatBankAmount(parsed)} {ccy}</span>
                                {last !== null && <span className="tabular-nums"> · {compactDelta(parsed - last)} vs last</span>}
                                {saved && <span className="text-green-700 font-medium"> · saved <Check className="inline w-4 h-4 -mt-0.5" /></span>}
                            </span>
                        ) : null}
                    </div>
                </>
            )}
        </li>
    );
}
